import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '@/integrations/supabase/client';
import type { ShopifyProduct } from '@/lib/shopify';

interface WishlistStore {
  items: ShopifyProduct[];
  localItems: ShopifyProduct[]; // For non-authenticated users
  isLoading: boolean;
  showLoginPrompt: boolean;
  pendingProduct: ShopifyProduct | null;
  
  loadWishlist: () => Promise<void>;
  addItem: (product: ShopifyProduct) => Promise<boolean>;
  addItemLocal: (product: ShopifyProduct) => void;
  removeItem: (productId: string) => Promise<void>;
  toggleItem: (product: ShopifyProduct) => Promise<boolean>;
  isInWishlist: (productId: string) => boolean;
  clearWishlist: () => Promise<void>;
  setShowLoginPrompt: (show: boolean) => void;
  setPendingProduct: (product: ShopifyProduct | null) => void;
  syncLocalToServer: () => Promise<void>;
}

export const useWishlistStore = create<WishlistStore>()(
  persist(
    (set, get) => ({
      items: [],
      localItems: [],
      isLoading: false,
      showLoginPrompt: false,
      pendingProduct: null,

      loadWishlist: async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          // Use local items for non-authenticated users
          return;
        }

        set({ isLoading: true });
        try {
          const { data, error } = await supabase
            .from('wishlist')
            .select('*')
            .eq('user_id', session.user.id)
            .order('added_at', { ascending: false });

          if (error) throw error;

          // Convert database records to ShopifyProduct format
          const products: ShopifyProduct[] = (data || []).map(item => ({
            id: item.product_handle,
            handle: item.product_handle,
            title: item.product_title,
            description: '',
            images: {
              edges: item.product_image ? [{
                node: {
                  url: item.product_image,
                  altText: item.product_title,
                }
              }] : []
            },
            priceRange: {
              minVariantPrice: {
                amount: item.product_price,
                currencyCode: 'ILS',
              },
            },
            variants: { edges: [] },
            options: [],
          }));

          set({ items: products });
          
          // Sync local items to server if any exist
          const { localItems } = get();
          if (localItems.length > 0) {
            await get().syncLocalToServer();
          }
        } catch (error) {
          console.error('Error loading wishlist:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      addItem: async (product) => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          // Show login prompt for non-authenticated users
          set({ showLoginPrompt: true, pendingProduct: product });
          return false;
        }

        const { items } = get();
        const exists = items.find(item => item.id === product.id);
        
        if (exists) {
          return false;
        }

        try {
          const { error } = await supabase
            .from('wishlist')
            .insert({
              user_id: session.user.id,
              product_handle: product.handle,
              product_title: product.title,
              product_price: product.priceRange.minVariantPrice.amount,
              product_image: product.images?.edges?.[0]?.node?.url || null,
            });

          if (error) {
            // Check for unique constraint violation
            if (error.code === '23505') {
              return false;
            }
            throw error;
          }

          set({ items: [...items, product] });
          return true;
        } catch (error) {
          console.error('Error adding to wishlist:', error);
          return false;
        }
      },

      addItemLocal: (product) => {
        const { localItems, items } = get();
        const existsLocal = localItems.find(item => item.id === product.id);
        const existsServer = items.find(item => item.id === product.id);
        
        if (existsLocal || existsServer) {
          return;
        }

        set({ 
          localItems: [...localItems, product],
          showLoginPrompt: false,
          pendingProduct: null
        });
      },

      removeItem: async (productId) => {
        const { data: { session } } = await supabase.auth.getSession();
        
        // Remove from local items first
        const { localItems } = get();
        const localExists = localItems.find(item => item.id === productId);
        if (localExists) {
          set({
            localItems: localItems.filter(item => item.id !== productId)
          });
        }
        
        if (!session) return;

        try {
          const { error } = await supabase
            .from('wishlist')
            .delete()
            .eq('user_id', session.user.id)
            .eq('product_handle', productId);

          if (error) throw error;

          set({
            items: get().items.filter(item => item.id !== productId)
          });
        } catch (error) {
          console.error('Error removing from wishlist:', error);
        }
      },

      toggleItem: async (product) => {
        const { items, localItems, addItem, removeItem, addItemLocal } = get();
        const existsInServer = items.find(item => item.id === product.id);
        const existsInLocal = localItems.find(item => item.id === product.id);
        
        if (existsInServer || existsInLocal) {
          await removeItem(product.id);
          return false; // removed
        } else {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const added = await addItem(product);
            return added;
          } else {
            // Add locally for non-authenticated users (no prompt needed)
            addItemLocal(product);
            return true;
          }
        }
      },

      isInWishlist: (productId) => {
        const { items, localItems } = get();
        return items.some(item => item.id === productId) || localItems.some(item => item.id === productId);
      },

      clearWishlist: async () => {
        const { data: { session } } = await supabase.auth.getSession();
        
        // Clear local items
        set({ localItems: [] });
        
        if (!session) return;

        try {
          const { error } = await supabase
            .from('wishlist')
            .delete()
            .eq('user_id', session.user.id);

          if (error) throw error;

          set({ items: [] });
        } catch (error) {
          console.error('Error clearing wishlist:', error);
        }
      },

      setShowLoginPrompt: (show) => set({ showLoginPrompt: show }),
      setPendingProduct: (product) => set({ pendingProduct: product }),

      syncLocalToServer: async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const { localItems, items } = get();
        
        for (const product of localItems) {
          const exists = items.find(item => item.id === product.id);
          if (!exists) {
            try {
              await supabase
                .from('wishlist')
                .insert({
                  user_id: session.user.id,
                  product_handle: product.handle,
                  product_title: product.title,
                  product_price: product.priceRange.minVariantPrice.amount,
                  product_image: product.images?.edges?.[0]?.node?.url || null,
                });
            } catch (error) {
              console.error('Error syncing item to server:', error);
            }
          }
        }

        // Clear local items after sync and reload from server
        set({ localItems: [] });
        await get().loadWishlist();
      },
    }),
    {
      name: 'alice-wishlist-local',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ localItems: state.localItems }),
    }
  )
);
